import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Search, MapPin, CalendarDays, Users, PawPrint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DESTS = ["Any destination", "Texas Hill Country", "Canyon Lake", "New Braunfels", "San Antonio"];

export const SearchBar = () => {
  const nav = useNavigate();
  const [dest, setDest] = useState("Any destination");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState("2");
  const [pets, setPets] = useState("0");

  const submit = () => {
    const params = new URLSearchParams();
    if (dest === "Canyon Lake" || dest === "San Antonio") params.set("q", dest);
    else if (dest === "New Braunfels") params.set("amenity", "new_braunfels");
    // "Texas Hill Country" / "Any destination" → no location filter (all homes are Hill Country)
    if (guests) params.set("guests", guests);
    if (pets !== "0" && dest !== "New Braunfels") params.set("amenity", "pet");
    nav(`/rentals?${params.toString()}`);
  };

  const dateLabel = range?.from
    ? range.to ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}` : format(range.from, "MMM d")
    : "Add dates";

  return (
    <div data-testid="hero-search" className="glass mx-auto flex w-full max-w-4xl flex-col gap-2 rounded-3xl border border-white/40 p-2.5 shadow-2xl md:flex-row md:items-center md:rounded-full">
      <div className="flex flex-1 items-center gap-2 rounded-2xl px-4 py-2 md:rounded-full">
        <MapPin size={18} className="text-camel" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="overline text-[9px] text-taupe">Destination</div>
          <Select value={dest} onValueChange={setDest}>
            <SelectTrigger data-testid="search-destination" className="h-6 border-0 bg-transparent p-0 text-sm font-medium shadow-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DESTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hidden h-9 w-px bg-black/10 md:block" />

      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="search-dates" className="flex flex-1 items-center gap-2 rounded-2xl px-4 py-2 text-left md:rounded-full">
            <CalendarDays size={18} className="text-camel" strokeWidth={1.5} />
            <div>
              <div className="overline text-[9px] text-taupe">Check in — out</div>
              <div className="text-sm font-medium">{dateLabel}</div>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2}
            disabled={{ before: new Date() }} />
        </PopoverContent>
      </Popover>

      <div className="hidden h-9 w-px bg-black/10 md:block" />

      <div className="flex flex-1 items-center gap-2 rounded-2xl px-4 py-2 md:rounded-full">
        <Users size={18} className="text-camel" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="overline text-[9px] text-taupe">Guests</div>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger data-testid="search-guests" className="h-6 border-0 bg-transparent p-0 text-sm font-medium shadow-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 4, 6, 8, 10, 12, 16].map((g) => <SelectItem key={g} value={String(g)}>{g} guests</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hidden h-9 w-px bg-black/10 md:block" />

      <div className="flex items-center gap-2 rounded-2xl px-4 py-2 md:rounded-full">
        <PawPrint size={18} className="text-camel" strokeWidth={1.5} />
        <div>
          <div className="overline text-[9px] text-taupe">Pets</div>
          <Select value={pets} onValueChange={setPets}>
            <SelectTrigger data-testid="search-pets" className="h-6 w-16 border-0 bg-transparent p-0 text-sm font-medium shadow-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["0", "1", "2"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button data-testid="search-submit" onClick={submit}
        className="h-12 rounded-full btn-ink px-6 md:h-14 md:w-14 md:px-0">
        <Search size={20} /> <span className="ml-2 md:hidden">Search</span>
      </Button>
    </div>
  );
};
